import { acceptedTypes, type ElementBindings, type ElementStylesDto, type ElementType } from '@xshop/shared';
import type { IconName, PageType, SettingField, SettingGroup } from '../../themes/registry';
import { ANY_PAGE } from '../../themes/pageScopes';

// What one element type *is*: a name a merchant recognises, one sentence saying what it is for, the
// controls its content is edited through, the look it lands in, and the rule the drag experience rests
// on — which types may be dropped inside it.

/** LightFunnels' five groups, which is how the Add panel files the catalog into one scannable list. */
export type ElementCategory = 'Texts' | 'Media' | 'Forms' | 'Layouts' | 'Other';

/** The pages the library may offer an element on. */
export type ElementBinds = { pages: readonly PageType[] };

/** One child a fresh drop arrives holding, named by type plus the props that make it say something. */
export type StarterChild = { type: ElementType; props?: SettingGroup };

/**
 * The content an element starts with, and the look it lands in, so a fresh drop is never an unstyled
 * box. `children` is for the elements whose content IS their children — a menu, tabs, a carousel — so
 * they land holding something to see instead of an invisible empty frame; the builder's add path reads
 * it through `newElementWithStarters`, and a definition that names none lands empty exactly as before.
 */
export type ElementDefaults = {
  props: SettingGroup;
  styles: ElementStylesDto;
  children?: readonly StarterChild[];
  /** What the element reads from the shop the moment it lands — a cart's count, a header's menu. */
  bindings?: ElementBindings;
};

export type ElementDefinition = {
  type: ElementType;
  label: string;
  /** One merchant-facing sentence, shown under the name in the Add panel. */
  description: string;
  category: ElementCategory;
  /** A lucide-react icon name, the same vocabulary the `icon` control already stores. */
  icon: IconName;
  /** The editable content fields, in the `SettingField` grammar the inspector already draws controls for. */
  props: readonly SettingField[];
  defaults: ElementDefaults;
  /** The types that may be dropped inside this one; empty for a leaf. */
  accepts: readonly ElementType[];
  /** A leaf that takes no children at all, so the drop layer never offers it an inside. */
  void?: true;
  binds: ElementBinds;
};

/** Everything about an element except the type its catalog row already names, and the two answers most give. */
export type ElementSpec = Omit<ElementDefinition, 'type' | 'void' | 'accepts' | 'binds'> & {
  binds?: ElementBinds;
};

/** Every page a theme can define — what all of the catalog needs saying, now that none of it reads a shop. */
const ANY_PAGE_BINDS: ElementBinds = { pages: ANY_PAGE };

// Says an element belongs on only some pages.
export const only = (pages: readonly PageType[]): ElementBinds => ({ pages });

// Builds one element definition, reading what it holds from the shared nesting rules the server checks
// too, and deriving `void` from an empty list so a leaf can never claim a drop zone the drag layer honours.
export function element(type: ElementType, spec: ElementSpec): ElementDefinition {
  const accepts = acceptedTypes(type);
  const base: ElementDefinition = { ...spec, type, accepts, binds: spec.binds ?? ANY_PAGE_BINDS };
  return accepts.length === 0 ? { ...base, void: true } : base;
}
